import { useParams, useNavigate } from '@tanstack/react-router';
import { Button, Divider, IconButton, ProductCard, Typography } from '@/primer/components';
import { useCampaign, useClaimCampaign } from '@/api';
import { PageColumn, PageLoadingState } from '@/components/layout';
import { getTaskDisplay } from '@/utils/taskTypes';

const stateColor: Record<string, 'success' | 'warning' | 'bodylight' | 'info'> = {
  invited: 'info',
  participating: 'success',
  completed: 'bodylight',
  claimed: 'warning',
};

const isTaskCompleted = (state: string | null): boolean =>
  state === 'completed' || state === 'approved';

export function CampaignDetail() {
  const { id: campaignId } = useParams({ from: '/_authenticated/campaign/$id' });
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const claimMutation = useClaimCampaign();

  const handleClaim = async () => {
    try {
      await claimMutation.mutateAsync({ campaignId, request: {} });
      navigate({ to: '/settings/address' });
    } catch (err) {
      console.error('Failed to claim campaign:', err);
    }
  };

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (error || !campaign) {
    return (
      <PageColumn className="py-6 flex flex-col gap-4">
        <Typography variant="header" size="h1" color="error" as="h1">
          Program Not Found
        </Typography>
        <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Home</Button>
      </PageColumn>
    );
  }

  const primaryProduct = campaign.products?.[0];
  const displayImage = campaign.imageUrl || primaryProduct?.productImageUrl;
  const displayName = campaign.name || primaryProduct?.productName || 'Program';
  const campaignState = campaign.state || 'invited';
  const statusColor = stateColor[campaignState] ?? 'info';

  return (
    <PageColumn className="py-4 flex flex-col gap-6">
      {/* Header row */}
      <div className="flex items-center gap-3">
        <div className="md:hidden">
          <IconButton
            icon="chevron-left"
            onClick={() => navigate({ to: '/dashboard' })}
            aria-label="Go back"
          />
        </div>
        <Typography variant="header" size="h1" as="h1">
          {displayName}
        </Typography>
      </div>

      {/* Campaign image */}
      {displayImage && (
        <img
          src={displayImage}
          alt={displayName}
          className="w-full aspect-video object-cover rounded-large"
        />
      )}

      {/* Status + meta */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          {campaign.campaignType && (
            <Typography variant="body" size="medium" color="bodylight">
              {campaign.campaignType}
            </Typography>
          )}
          <Typography variant="body" size="medium" color={statusColor} decoration={{ bold: true }}>
            {campaignState.charAt(0).toUpperCase() + campaignState.slice(1)}
          </Typography>
        </div>

        <div className="flex gap-6">
          <div className="flex flex-col gap-1">
            <Typography variant="body" size="small" color="bodylight">
              Tasks
            </Typography>
            <Typography variant="body" size="large" as="span" decoration={{ bold: true }}>
              {campaign.completedTaskCount} / {campaign.totalTaskCount}
            </Typography>
          </div>
          {campaign.expired && (
            <div className="flex flex-col gap-1">
              <Typography variant="body" size="small" color="bodylight">
                Expires
              </Typography>
              <Typography variant="body" size="large" as="span" decoration={{ bold: true }}>
                {new Date(campaign.expired).toLocaleDateString()}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {campaign.description && (
        <>
          <Divider />
          <div className="flex flex-col gap-2">
            <Typography variant="header" size="h2" as="h2">
              Description
            </Typography>
            <Typography variant="body" size="medium">
              {campaign.description}
            </Typography>
          </div>
        </>
      )}

      {/* Products */}
      {campaign.products && campaign.products.length > 0 && (
        <>
          <Divider />
          <div className="flex flex-col gap-3">
            <Typography variant="header" size="h2" as="h2">
              Products
            </Typography>
            {campaign.products.map((product) => (
              <div key={product.casProductId} className="flex flex-col gap-2">
                <ProductCard
                  imageUrl={product.productImageUrl ?? '/assets/images/placeholder.svg'}
                  imageAlt={product.productImageUrl ? (product.productName ?? 'Product') : ''}
                  brandName={product.productBrand ?? ''}
                  productName={product.productName ?? 'Product'}
                />
                {product.fairMarketValue != null && (
                  <Typography variant="body" size="small" color="body">
                    ${product.fairMarketValue} {product.fairMarketValueCurrency}
                  </Typography>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tasks */}
      {campaign.tasks && campaign.tasks.length > 0 && (
        <>
          <Divider />
          <div className="flex flex-col gap-3">
            <Typography variant="header" size="h2" as="h2">
              Tasks
            </Typography>
            <ul className="flex flex-col gap-2 list-none p-0 m-0">
              {campaign.tasks.map((task) => {
                const completed = isTaskCompleted(task.state);
                return (
                  <li key={task.id} className="flex items-start gap-3">
                    <Typography
                      variant="body"
                      size="medium"
                      color={completed ? 'success' : 'bodylight'}
                    >
                      {completed ? '✓' : '○'}
                    </Typography>
                    <div className="flex flex-col gap-1">
                      <Typography
                        variant="body"
                        size="medium"
                        color={completed ? 'bodylight' : 'header'}
                      >
                        {getTaskDisplay(task.type).name}
                      </Typography>
                      {task.required && (
                        <Typography variant="body" size="small" color="error">
                          Required
                        </Typography>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      {/* Terms */}
      {campaign.terms && (
        <>
          <Divider />
          <div className="flex flex-col gap-2">
            <Typography variant="header" size="h2" as="h2">
              Terms &amp; Conditions
            </Typography>
            <Typography variant="body" size="small">
              {new DOMParser().parseFromString(campaign.terms, 'text/html').body.textContent}
            </Typography>
          </div>
        </>
      )}

      {/* CTA */}
      {campaignState === 'invited' && (
        <Button fullWidth size="large" onClick={handleClaim} isLoading={claimMutation.isPending}>
          Claim This Program
        </Button>
      )}

      {claimMutation.error && (
        <Typography variant="body" size="small" color="error" align="center">
          Failed to claim program. Please try again.
        </Typography>
      )}
    </PageColumn>
  );
}

export default CampaignDetail;
